import { Check } from 'lucide-react';
import * as React from 'react';
import { Logo } from './logo';

// Split layout for every signed-out screen (sign-in, sign-up, email link, authenticator set-up): docs/06 §4 and the
// prototype's P-C0 — a narrow white form on the left, the forest panel on the right with an eyebrow, a headline, the
// value points with icons and the network motif from the Slingshot deck.

export type AuthPoint = string | { title: string; text?: string; icon?: React.ReactNode };

export function AuthLayout({ title, intro, points, children, footer, eyebrow, headline }: {
  title: string; intro?: React.ReactNode; points: AuthPoint[]; children: React.ReactNode; footer?: React.ReactNode;
  eyebrow?: string; headline?: string;
}) {
  return (
    <div className="grid min-h-full lg:grid-cols-[minmax(380px,440px)_minmax(0,1fr)]">
      <main className="flex flex-col bg-surface px-6 py-10 sm:px-12">
        <div className="mx-auto w-full max-w-sm flex-1 lg:mx-0">
          <Logo variant="dark" className="h-7 w-auto" />
          <h1 className="mt-10 font-display text-[32px] font-bold leading-10 text-ink">{title}</h1>
          {intro && <div className="mt-1 text-ink-2">{intro}</div>}
          {children}
          {footer && <div className="mt-8 text-sm text-ink-2">{footer}</div>}
        </div>
        <p className="mx-auto mt-10 w-full max-w-sm text-xs leading-5 text-ink-3 lg:mx-0">
          Paystreet Limited is applying for a Money Service Operator licence (C&amp;ED).
        </p>
      </main>
      <aside className="relative hidden overflow-hidden bg-[radial-gradient(120%_90%_at_0%_0%,var(--color-moss)_0%,var(--color-forest)_45%,var(--color-night)_100%)] px-14 py-16 text-white lg:flex lg:flex-col lg:justify-center">
        <div className="relative z-10 max-w-xl">
          {eyebrow ? <p className="text-[11px] font-semibold uppercase tracking-[.12em] text-lime">{eyebrow}</p> : <Logo variant="white" className="h-8 w-auto" />}
          {headline && <p className="mt-4 font-display text-[34px] font-bold leading-[42px]">{headline}</p>}
          <ul className="mt-10 space-y-6">
            {points.map(p => {
              const point = typeof p === 'string' ? { title: p } : p;
              return (
                <li key={point.title} className="flex gap-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-lime" aria-hidden>{point.icon ?? <Check className="size-4" />}</span>
                  <span className="pt-0.5">
                    <span className={point.text ? 'block font-semibold text-white' : 'block text-[15px] text-white/90'}>{point.title}</span>
                    {point.text && <span className="mt-0.5 block text-sm text-white/75">{point.text}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <NetworkMotif />
      </aside>
    </div>
  );
}

/** The deck's network motif: thin lines and nodes in the tick green, bottom right, decorative only. */
function NetworkMotif() {
  const nodes: Array<[number, number, number]> = [[150, 10, 3], [300, 45, 5], [110, 75, 4], [205, 112, 4], [12, 150, 4], [260, 182, 3]];
  const lines: Array<[number, number]> = [[0, 2], [0, 1], [2, 3], [3, 1], [2, 4], [3, 5], [1, 6]];
  const pts = [...nodes, [360, 90, 0] as [number, number, number]];
  return (
    <svg aria-hidden className="pointer-events-none absolute bottom-6 right-0 h-48 w-96 text-tick opacity-80" viewBox="0 0 360 200" fill="none">
      {lines.map(([a, b], i) => <line key={i} x1={pts[a]![0]} y1={pts[a]![1]} x2={pts[b]![0]} y2={pts[b]![1]} stroke="currentColor" strokeOpacity=".55" strokeWidth="1" />)}
      {nodes.map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} fill="currentColor" />)}
    </svg>
  );
}

/** Text link styled as an inline action (sign-in ↔ sign-up, resend link). */
export function TextButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className="font-semibold text-tick-ink hover:underline disabled:opacity-50" {...props}>{children}</button>;
}
